import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import { fetchMyDepartment, resolveAvatarSrc } from '../services/api';
import Avatar from '../components/Avatar';
import Chip from '../components/Chip';
import type { PersonSummary } from '../types';

function PersonCard({ person, onClick }: { person: PersonSummary; onClick: () => void }) {
  return (
    <div className="sk-emp-card" onClick={onClick}>
      <Avatar firstName={person.firstName} lastName={person.lastName} src={resolveAvatarSrc(person.avatarUrl)} size="lg" />
      <div className="emp-name">{person.firstName} {person.lastName}</div>
      <div className="emp-role">{person.jobTitle || '—'}</div>
      {person.departmentName && (
        <div style={{ marginTop: 8 }}>
          <Chip variant="neutral">{person.departmentName}</Chip>
        </div>
      )}
    </div>
  );
}

export default function MyDepartment() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: qk.myDepartment(), queryFn: fetchMyDepartment });

  const goTo = (id: string) => navigate(`/profile/${id}`);

  if (isLoading) {
    return <div style={{ padding: 48, color: 'var(--ink-3)' }}>Loading…</div>;
  }

  return (
    <div>
      <div className="sk-page-header">
        <div>
          <h1>My department</h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 'var(--fs-sm)', marginTop: 4 }}>
            {data?.department?.name ?? 'No department assigned yet'}
          </p>
        </div>
      </div>

      {/* Reporting manager */}
      <div style={{ marginBottom: 24 }}>
        <div className="card-head" style={{ marginBottom: 8 }}><h3>Reporting manager</h3></div>
        {data?.manager ? (
          <div className="emp-grid">
            <PersonCard person={data.manager} onClick={() => goTo(data.manager!.id)} />
          </div>
        ) : (
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>No reporting manager on record.</p>
        )}
      </div>

      {/* HR contact */}
      <div style={{ marginBottom: 24 }}>
        <div className="card-head" style={{ marginBottom: 8 }}><h3>HR contact</h3></div>
        {data && data.hr.length > 0 ? (
          <div className="emp-grid">
            {data.hr.map((person) => (
              <PersonCard key={person.id} person={person} onClick={() => goTo(person.id)} />
            ))}
          </div>
        ) : (
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>No HR contact found.</p>
        )}
      </div>

      {/* Department colleagues */}
      <div>
        <div className="card-head" style={{ marginBottom: 8 }}><h3>Department colleagues</h3></div>
        {data && data.colleagues.length > 0 ? (
          <div className="emp-grid">
            {data.colleagues.map((person) => (
              <PersonCard key={person.id} person={person} onClick={() => goTo(person.id)} />
            ))}
          </div>
        ) : (
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>
            {data?.department ? 'No other colleagues in your department yet.' : 'Assign a department to see colleagues here.'}
          </p>
        )}
      </div>
    </div>
  );
}
